export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Prevents entering more than `maxDecimals` decimal places, a second decimal point,
 * or exponential notation and signs ('e', 'E', '+', '-') in an input field.
 * Note: This key-down handler provides UX polish; true enforcement is performed
 * by `limitDecimalPlaces` in `onChange` and schema validation on form submit.
 */
export const handleDecimalKeyDown = (e, currentValue, maxDecimals = 2) => {
  if (
    ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key) ||
    e.ctrlKey ||
    e.metaKey
  ) {
    return;
  }
  // Block exponential notation and sign symbols allowed by HTML type="number" inputs
  if (["e", "E", "+", "-"].includes(e.key)) {
    e.preventDefault();
    return;
  }
  if (e.key === "." && (maxDecimals <= 0 || String(currentValue ?? "").includes("."))) {
    e.preventDefault();
    return;
  }
  if (/^\d$/.test(e.key)) {
    const input = e.target;
    let selStart = null;
    let selEnd = null;
    try {
      if (typeof input?.selectionStart === "number") {
        selStart = input.selectionStart;
        selEnd = input.selectionEnd;
      }
    } catch {
      // In browsers like Chrome, accessing selectionStart on type="number" throws DOMException
    }
    if (selStart !== null && selStart !== selEnd) return;
    const val = String(currentValue ?? input?.value ?? "");
    const dotIndex = val.indexOf(".");
    if (dotIndex !== -1 && selStart !== null && selStart > dotIndex) {
      const decPart = val.slice(dotIndex + 1);
      if (decPart.length >= maxDecimals) {
        e.preventDefault();
      }
    }
  }
};

/**
 * Truncates string/number representation to at most `maxDecimals` decimal places.
 */
export const limitDecimalPlaces = (val, maxDecimals = 2) => {
  if (val === "" || val === null || val === undefined) return val;
  const str = String(val);
  if (str.includes(".")) {
    const [intPart, decPart] = str.split(".");
    if (decPart && decPart.length > maxDecimals) {
      return `${intPart}.${decPart.slice(0, maxDecimals)}`;
    }
  }
  return str;
};

/**
 * Rounds a number to at most 2 decimal places.
 */
export const roundToTwoDecimals = (val) => {
  if (val === "" || val === null || val === undefined) return val;
  const num = Number(val);
  return Number.isFinite(num) ? Number(num.toFixed(2)) : val;
};

export const roundToNearestFifty = (num, nearestTo = 50) => {
  return Math.round(num / nearestTo) * nearestTo;
}